import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';
import clsx from 'clsx';
import { Spinner } from './Spinner';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
}

// Figma UI kit, Buttons (node 1:677). Strokes are inset rings so bordered variants keep the same height.
const variants: Record<Variant, string> = {
  primary: 'bg-primary text-primary-fg not-disabled:hover:bg-primary-hover',
  secondary: 'bg-primary-subtle text-primary not-disabled:hover:bg-primary-subtle-hover',
  outline: 'bg-transparent text-fg ring-1 ring-fg ring-inset not-disabled:hover:bg-fg/8',
  ghost: 'bg-transparent text-fg-secondary not-disabled:hover:bg-fg/8 not-disabled:hover:text-fg',
  danger:
    'bg-danger-subtle text-danger-text ring-1 ring-danger-line ring-inset ' +
    'not-disabled:hover:bg-danger-subtle-hover not-disabled:hover:ring-danger-text',
};

const sizes: Record<Size, string> = {
  sm: 'px-3.5 py-2 text-xs/4',
  md: 'px-5 py-4 text-xs/4',
  lg: 'px-8 py-5 text-sm/[21px]',
};

// Disabled ("Inert") looks the same for every variant; a loading button keeps its variant colors.
const base =
  'inline-flex shrink-0 items-center justify-center gap-2 rounded-full font-semibold tracking-label uppercase ' +
  'whitespace-nowrap transition duration-150 select-none aria-busy:cursor-wait ' +
  'disabled:not-aria-busy:cursor-not-allowed disabled:not-aria-busy:bg-surface-raised disabled:not-aria-busy:text-fg-muted ' +
  'disabled:not-aria-busy:opacity-40 disabled:not-aria-busy:ring-0';

export function buttonClass(variant: Variant = 'primary', size: Size = 'md', className?: string): string {
  return clsx(base, variants[variant], sizes[size], className);
}

/** A router link that looks like a button (avoids nesting <button> inside <a>). */
export function LinkButton({
  variant,
  size,
  icon,
  className,
  children,
  ...rest
}: LinkProps & { variant?: Variant; size?: Size; icon?: ReactNode }) {
  return (
    <Link className={buttonClass(variant, size, className)} {...rest}>
      {icon}
      {children}
    </Link>
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, icon, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={buttonClass(variant, size, className)}
      {...rest}
    >
      {loading ? <Spinner className="size-3" /> : icon}
      {children}
    </button>
  );
});
